import { useState, useEffect } from "react";
import {
  Calendar,
  BarChart3,
  PieChart,
  Download,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Button,
  DataTable,
} from "@b2b/ui-components";
import { rfqService } from "@b2b/services";

type DateRange = "7days" | "30days" | "90days" | "1year";

interface RfqMetrics {
  total: number;
  byStatus: Record<string, number>;
  avgQuotesPerRfq: number;
  avgTimeToFirstQuoteHours: number | null;
}

interface RfqSummaryRow {
  status: string;
  count: number;
  percentage: string;
}

const AdminRfqReports = () => {
  const [dateRange, setDateRange] = useState<DateRange>("30days");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const [metrics, setMetrics] = useState<RfqMetrics | null>(null);

  const [statusSummary, setStatusSummary] = useState<RfqSummaryRow[]>([]);

  const [exporting, setExporting] = useState(false);

  // Derive start/end dates for RFQ metrics based on dateRange selection
  const getDateRangeParams = (range: DateRange): { startDate?: string; endDate?: string } => {
    const end = new Date();
    let start = new Date();

    switch (range) {
      case "7days":
        start.setDate(end.getDate() - 7);
        break;
      case "30days":
        start.setDate(end.getDate() - 30);
        break;
      case "90days":
        start.setDate(end.getDate() - 90);
        break;
      case "1year":
        start.setFullYear(end.getFullYear() - 1);
        break;
      default:
        start.setDate(end.getDate() - 30);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const buildStatusSummary = (rfqMetrics: RfqMetrics) => {
    const total = rfqMetrics.total || 0;
    const entries = Object.entries(rfqMetrics.byStatus || {});

    const rows: RfqSummaryRow[] = entries.map(([status, count]) => {
      const pct = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
      return {
        status,
        count,
        percentage: `${pct}%`,
      };
    });

    setStatusSummary(rows);
  };

  const fetchRfqMetrics = async () => {
    // Wait for token to be available before making API calls (prevents cancellation for Auth0 users)
    const { getAccessToken } = await import("@b2b/services");
    const token = await getAccessToken();
    if (!token) {
      console.log("⏳ AdminRfqReports: Token not available yet, waiting...");
      // Retry after a short delay
      setTimeout(() => {
        fetchRfqMetrics();
      }, 500);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log("📞 AdminRfqReports: Fetching RFQ metrics...");

      const { startDate, endDate } = getDateRangeParams(dateRange);

      const data = await rfqService.getRfqMetrics({
        startDate,
        endDate,
      });

      setMetrics(data);
      buildStatusSummary(data);
      console.log("✅ AdminRfqReports: RFQ metrics fetched successfully");
    } catch (err: any) {
      console.error("❌ AdminRfqReports: Error fetching RFQ metrics:", err);
      const message = err?.message || "Failed to load RFQ reports";
      setError(message);
      setToast({
        type: "error",
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setToast({ type: "success", message: "Exporting RFQ data..." });

      const { startDate, endDate } = getDateRangeParams(dateRange);

      const blob = await rfqService.exportRfqs({
        startDate,
        endDate,
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rfq-reports-${dateRange}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setToast({
        type: "success",
        message: "RFQ data exported successfully",
      });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err?.message || "Failed to export RFQ data",
      });
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchRfqMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const formatNumber = (value: number): string => {
    return value.toLocaleString();
  };

  return (
    <div className="space-y-8 mt-5">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            toast.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toast.message}
          <button
            onClick={() => setToast(null)}
            className="ml-4 hover:opacity-80"
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">RFQ Reports</h1>
          <p className="text-muted-foreground">
            RFQ-specific analytics for monitoring demand, response quality, and turnaround time.
          </p>
        </div>

        <div className="flex items-center space-x-4">
          <Select value={dateRange} onValueChange={(value) => setDateRange(value as DateRange)}>
            <SelectTrigger className="w-48">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white ">
              <SelectItem value="7days" className="hover:bg-gray-100">
                Last 7 days
              </SelectItem>
              <SelectItem value="30days" className="hover:bg-gray-100">
                Last 30 days
              </SelectItem>
              <SelectItem value="90days" className="hover:bg-gray-100">
                Last 90 days
              </SelectItem>
              <SelectItem value="1year" className="hover:bg-gray-100">
                Last year
              </SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="primary"
            onClick={handleExport}
            disabled={loading || exporting}
          >
            <Download className="h-4 w-4 mr-2" />
            Export RFQs
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground text-lg">
            Loading RFQ reports...
          </p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-16">
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <Button onClick={fetchRfqMetrics} variant="outline">
            Try again
          </Button>
        </div>
      )}

      {/* Content */}
      {!loading && !error && metrics && (
        <>
          {/* Top RFQ KPIs */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total RFQs</p>
                    <p className="text-3xl font-bold text-foreground mt-2">
                      {formatNumber(metrics.total)}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <BarChart3 className="h-6 w-6 text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Avg Quotes per RFQ
                    </p>
                    <p className="text-3xl font-bold text-foreground mt-2">
                      {metrics.avgQuotesPerRfq.toFixed(1)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Avg Time to First Quote
                    </p>
                    <p className="text-3xl font-bold text-foreground mt-2">
                      {metrics.avgTimeToFirstQuoteHours
                        ? `${metrics.avgTimeToFirstQuoteHours.toFixed(1)}h`
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      Status Buckets
                    </p>
                    <p className="text-3xl font-bold text-foreground mt-2">
                      {Object.keys(metrics.byStatus || {}).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RFQ Status Distribution */}
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  RFQ Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {statusSummary.length > 0 ? (
                  <div className="space-y-2">
                    {statusSummary.map((row) => (
                      <div
                        key={row.status}
                        className="flex items-center justify-between border rounded-lg px-3 py-2"
                      >
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{row.status}</Badge>
                          <span className="text-sm text-muted-foreground">
                            {row.percentage} of RFQs
                          </span>
                        </div>
                        <div className="text-sm font-semibold">
                          {formatNumber(row.count)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No RFQ data available for the selected period
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Simple tabular view for quick export confirmation */}
            <Card>
              <CardHeader>
                <CardTitle>RFQ Status Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {statusSummary.length > 0 ? (
                  <DataTable
                    data={statusSummary}
                    config={{
                      columns: [
                        { key: "status", label: "Status" },
                        { key: "count", label: "Count" },
                        { key: "percentage", label: "Share" },
                      ],
                      styles: {
                        headerBg: "bg-gray-100",
                        headerText: "text-gray-700",
                        rowHover: "hover:bg-gray-50",
                      },
                    }}
                    rowsPerPage={5}
                  />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No RFQ data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminRfqReports;





